import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { seoMeta as seoEN } from '../data/seo_en';
import { seoMeta as seoSL } from '../data/seo_sl';

const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;

// Slug mapping for hreflang alternates
const EN_TO_SL_SLUGS = {
  '/': '/sl/',
  '/about/': '/sl/o-nas/',
  '/services/': '/sl/storitve/',
  '/projects/': '/sl/projekti/',
};

const SL_TO_EN_SLUGS = Object.fromEntries(
  Object.entries(EN_TO_SL_SLUGS).map(([en, sl]) => [sl, en])
);

// Helper: Normalize path to always end with /
const normalizePath = (pathname) => {
  return pathname.endsWith('/') ? pathname : `${pathname}/`;
};

// Helper: Get alternate language paths
const getAlternatePaths = (path, lang) => {
  if (lang === 'sl') {
    return {
      enPath: SL_TO_EN_SLUGS[path] || '/',
      slPath: path,
    };
  }
  return {
    enPath: path,
    slPath: EN_TO_SL_SLUGS[path] || '/sl/',
  };
};

// Helper: Build organization schema
const buildOrgSchema = (lang) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'go2digital',
  alternateName: 'Go2 Digital',
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.svg`,
  description: lang === 'sl'
    ? 'Slovenska agencija za digitalne platforme, SEO in partnerski marketing.'
    : 'Performance-driven digital platform builder specializing in SEO and affiliate marketing.',
  foundingDate: '2022',
  founder: {
    '@type': 'Person',
    name: 'Matevz R.',
  },
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'SI',
  },
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'info@go2digital.com',
    contactType: 'business inquiries',
  },
});

// Helper: Build website schema
const buildWebsiteSchema = (lang) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'go2digital',
  url: SITE_URL,
  inLanguage: lang === 'sl' ? 'sl-SI' : 'en',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/services/`,
    },
    'query-input': 'required name=search_term_string',
  },
});

// Helper: Build breadcrumb schema
const buildBreadcrumbSchema = (path, lang) => {
  const breadcrumbs = [];
  
  // Always add home
  breadcrumbs.push({
    '@type': 'ListItem',
    position: 1,
    name: lang === 'sl' ? 'Domov' : 'Home',
    item: SITE_URL + (lang === 'sl' ? '/sl/' : '/'),
  });

  // Add page-specific breadcrumbs
  if (path.includes('/about') || path.includes('/o-nas')) {
    breadcrumbs.push({
      '@type': 'ListItem',
      position: 2,
      name: lang === 'sl' ? 'O nas' : 'About',
      item: `${SITE_URL}${path}`,
    });
  } else if (path.includes('/services') || path.includes('/storitve')) {
    breadcrumbs.push({
      '@type': 'ListItem',
      position: 2,
      name: lang === 'sl' ? 'Storitve' : 'Services',
      item: `${SITE_URL}${path}`,
    });
  } else if (path.includes('/projects') || path.includes('/projekti')) {
    breadcrumbs.push({
      '@type': 'ListItem',
      position: 2,
      name: lang === 'sl' ? 'Projekti' : 'Projects',
      item: `${SITE_URL}${path}`,
    });
  }

  return breadcrumbs.length > 1 ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs,
  } : null;
};

const SEOHead = () => {
  const location = useLocation();
  const { lang } = useLanguage();

  // Memoize computed values
  const seoData = useMemo(() => {
    const path = normalizePath(location.pathname);
    const allMeta = lang === 'sl' ? seoSL : seoEN;
    const meta = allMeta[path] || Object.values(allMeta)[0];
    const canonicalUrl = `${SITE_URL}${path}`;
    const { enPath, slPath } = getAlternatePaths(path, lang);
    
    return {
      meta,
      canonicalUrl,
      enUrl: `${SITE_URL}${enPath}`,
      slUrl: `${SITE_URL}${slPath}`,
      orgSchema: buildOrgSchema(lang),
      websiteSchema: buildWebsiteSchema(lang),
      breadcrumbSchema: buildBreadcrumbSchema(path, lang),
    };
  }, [location.pathname, lang]);

  const { meta, canonicalUrl, enUrl, slUrl, orgSchema, websiteSchema, breadcrumbSchema } = seoData;

  return (
    <Helmet>
      {/* Language */}
      <html lang={lang === 'sl' ? 'sl' : 'en'} />

      {/* Title & Description */}
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang alternates */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="sl" href={slUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="go2digital" />
      <meta property="og:locale" content={lang === 'sl' ? 'sl_SI' : 'en_US'} />
      <meta property="og:locale:alternate" content={lang === 'sl' ? 'en_US' : 'sl_SI'} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="go2digital" />
      <meta name="geo.region" content="SI" />
      <meta name="geo.placename" content="Slovenia" />

      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
    </Helmet>
  );
};

export default SEOHead;
